import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Button } from 'react-bootstrap';
import { Clipboard, Pencil, PinAngle, Plus, Printer, Trash } from 'react-bootstrap-icons';
import { toast } from 'react-toastify';
import { makeApiRequests } from '../../../helpers/api';
import { ENDPOINTS } from '../../../helpers/constants';
import {
  getInventoryUnitId,
  isInventoryUnitExcluded,
  withInventoryStatus
} from '../../../helpers/inventoryUnitHelpers';
import { SLOT_STATUS, copyInventoryDetails } from '../../../helpers/global';
import CalculationWorksheetSidebar from '../../pipeline/CalculationWorksheetSidebar';
import CommissionSheetSidebar from './CommissionSheetSidebar';
import AlertModal from '../../AlertModal';
import CustomMultiSelect from '../../common/CustomMultiSelect';
import GoogleMap from '../../common/google-map/GoogleMap';
import InventoryPicker from '../../inventory/InventoryPicker';
import InventorySearchItem from '../../inventory/InventorySearchItem';
import SlotNumberModal from '../../main/SlotNumberModal';
import { isAdmin } from '../../../helpers/roleHelpers';
import SiteMapPickerSlotPanel from './SiteMapPickerSlotPanel';
import SiteMapVirtualTourModal from './SiteMapVirtualTour';

const getModelId = model => {
  if (!model) return null;
  if (typeof model === 'string') return model;
  return model._id ? String(model._id) : null;
};

const SiteMap = ({ site, onSiteUpdate, pickerMode = false, onPickerUnitSelect, excludedUnitIds = [], onPrint }) => {
  const [newMapProperties, setNewMapProperties] = useState(null);
  const [siteAddingUpdating, setSiteAddingUpdating] = useState(false);

  const [selectedPolygonInfo, setSelectedPolygonInfo] = useState(null);

  const [inventoryUnitMeta, setInventoryUnitMeta] = useState(null);
  const [deleteSlotMeta, setDeleteSlotMeta] = useState(null);

  const [createSlotModalMeta, setCreateSlotModalMeta] = useState(null);
  const [progressState, setProgressState] = useState(false);
  const [isOfficeBuilding, setIsOfficeBuilding] = useState(false);
  const [showCalculationWorksheet, setShowCalculationWorksheet] = useState(false);
  const [worksheetInventory, setWorksheetInventory] = useState(null);
  const [showCommissionSheet, setShowCommissionSheet] = useState(false);
  const [showVirtualTourModal, setShowVirtualTourModal] = useState(false);
  const [virtualTourOverrides, setVirtualTourOverrides] = useState({});
  const siteRef = useRef(site);
  siteRef.current = site;

  const appChoices = useMemo(() => {
    try {
      return JSON.parse(localStorage.getItem('app-choices') || '[]');
    } catch {
      return [];
    }
  }, []);

  const selectedSlotInventory = useMemo(() => {
    const slot = site?.slots?.[selectedPolygonInfo?.polygonIndex];
    return slot?.inventory || null;
  }, [site, selectedPolygonInfo]);

  const selectedModelId = useMemo(() => getModelId(selectedSlotInventory?.model), [selectedSlotInventory]);

  const modelVirtualTourLink = useMemo(() => {
    const inventory = selectedSlotInventory;
    if (!inventory) return null;

    const modelId = getModelId(inventory.model);
    if (modelId && Object.prototype.hasOwnProperty.call(virtualTourOverrides, modelId)) {
      const overridden = virtualTourOverrides[modelId];
      return overridden && String(overridden).trim() ? String(overridden).trim() : null;
    }

    const directLink = inventory.model?.virtualTourLink;
    if (directLink && String(directLink).trim()) {
      return String(directLink).trim();
    }

    if (!modelId) return null;

    const models = appChoices.find(a => a.key === 'model')?.values || [];
    const model = models.find(m => String(m._id) === String(modelId));
    const link = model?.virtualTourLink;
    return link && String(link).trim() ? String(link).trim() : null;
  }, [selectedSlotInventory, appChoices, virtualTourOverrides]);

  const onInventoryPickerClose = () => {
    setInventoryUnitMeta(null);
  };

  const updateSiteDefaultView = async () => {
    let objectToUpdate = {
      coordinates: newMapProperties.center,
      rotation: newMapProperties.heading,
      scale: newMapProperties.zoom
    };

    setSiteAddingUpdating(true);

    const { response, error } = await makeApiRequests({
      endpoint: ENDPOINTS.SITE_WITH_ID(site._id),
      requestBody: { ...objectToUpdate },
      method: 'PUT'
    });

    setSiteAddingUpdating(false);
    if (error) {
      toast.error(error);
      return;
    }

    toast.success('Default view updated!');
    onSiteUpdate(response);
    setNewMapProperties(null);
  };

  const handlePolygonClick = (polygonIndex, event) => {
    let selectedSlot = site?.slots?.[polygonIndex] || null;
    setShowVirtualTourModal(false);
    setSelectedPolygonInfo({ polygonIndex, polygon: event, selectedSlot });
  };

  useEffect(() => {
    if (!selectedPolygonInfo) setShowVirtualTourModal(false);
  }, [selectedPolygonInfo]);

  const onAddNewPolygon = async (paths, action = 'create') => {
    setCreateSlotModalMeta({
      paths,
      modalAction: action
    });
  };

  const cloneSlot = async selectedSlot => {
    if (!selectedSlot?.paths) return;

    const clonedPaths = selectedSlot.paths.map(path => ({
      lat: path.lat,
      lng: path.lng + 0.0002
    }));

    onAddNewPolygon(clonedPaths, 'clone');
  };

  const handleSlotNumberSubmit = async (slotNumber, existingSlot = null) => {
    let slotToAdd = {
      site: site._id,
      paths: createSlotModalMeta.paths || existingSlot?.paths,
      number: slotNumber,
      isOfficeBuilding: isOfficeBuilding,
      status: existingSlot?.status,
      inventory: existingSlot?.inventory
    };

    setProgressState(true);
    setSiteAddingUpdating(true);
    const { response, error } = await makeApiRequests({
      endpoint: existingSlot ? ENDPOINTS.SLOT_WITH_ID(existingSlot._id) : ENDPOINTS.SLOT_BASE,
      requestBody: { ...slotToAdd },
      method: existingSlot ? 'PUT' : 'POST'
    });

    setSiteAddingUpdating(false);
    if (error) {
      toast.error(error);
      setProgressState(false);
      return;
    }

    if (existingSlot) {
      toast.success('Updated slot');
      onSiteUpdate({
        ...site,
        slots: site.slots.map(s => (s._id === response._id ? response : s))
      });
      setProgressState(false);
      setCreateSlotModalMeta(null);
    } else {
      toast.success('Added a new slot');
      onSiteUpdate({ ...site, slots: [...site.slots, response] });
      setProgressState(false);
      setCreateSlotModalMeta(null);
    }
  };

  const onDeleteSlotClick = slotToDelete => {
    setDeleteSlotMeta({ slotToDelete });
  };

  const deleteSlot = async () => {
    setSiteAddingUpdating(true);
    let { slotToDelete: polygon } = deleteSlotMeta;
    setDeleteSlotMeta({ ...deleteSlotMeta, showProgress: true });

    const { response, error } = await makeApiRequests({
      endpoint: ENDPOINTS.SLOT_WITH_ID(polygon._id),
      method: 'DELETE'
    });

    setSiteAddingUpdating(false);
    if (error) {
      setDeleteSlotMeta({ ...deleteSlotMeta, showProgress: false });
      toast.error(error);
      return;
    }

    toast.success('Slot has been deleted');
    onSiteUpdate({ ...site, slots: site.slots.filter(s => s._id !== polygon._id) });
    setSelectedPolygonInfo(null);
    setDeleteSlotMeta(null);
  };

  const onPolygonUpdate = async (polygon, callback) => {
    let slotToUpdate = {
      ...polygon
    };

    setSiteAddingUpdating(true);

    const { response: updatedSlot, error } = await makeApiRequests({
      endpoint: ENDPOINTS.SLOT_WITH_ID(polygon._id),
      requestBody: slotToUpdate,
      method: 'PUT'
    });

    setSiteAddingUpdating(false);
    if (error) {
      toast.error(error);
      return;
    }

    toast.success('Updated slot');
    const updatedSlotInventoryUnit = updatedSlot?.inventory;
    onSiteUpdate({
      ...site,
      slots: site.slots.map(s =>
        s._id === updatedSlot._id
          ? updatedSlot
          : {
              ...s,
              inventory:
                //clear new inventory unit from other slots
                updatedSlotInventoryUnit && updatedSlotInventoryUnit._id === s.inventory?._id ? null : s.inventory
            }
      )
    });
    if (callback) {
      callback();
    }
  };

  const assignUnitToSlot = () => {
    let slotToUpdate = inventoryUnitMeta?.slot;
    let inventory = inventoryUnitMeta?.selectedUnit;

    onPolygonUpdate({ ...slotToUpdate, inventory: inventory?._id }, () => {
      setInventoryUnitMeta(null);
      // setSelectedPolygonInfo(null);
    });
  };

  const removeUnitFromSlot = slot => {
    onPolygonUpdate({ ...slot, inventory: null });
  };

  const handleCopyUnit = () => {
    if (!selectedSlotInventory) return;
    copyInventoryDetails(selectedSlotInventory);
    toast.success('Copied!');
  };

  const handleOpenCalculationWorksheet = () => {
    if (!selectedSlotInventory) return;
    setWorksheetInventory(selectedSlotInventory);
    setShowCalculationWorksheet(true);
  };

  const handleOpenCommissionSheet = () => {
    if (!selectedSlotInventory) return;
    setShowCommissionSheet(true);
  };

  const handleVirtualTourSaved = useCallback(
    (modelId, link) => {
      const currentSite = siteRef.current;
      setVirtualTourOverrides(prev => ({ ...prev, [String(modelId)]: link }));
      onSiteUpdate({
        ...currentSite,
        slots: (currentSite.slots || []).map(slot => {
          const slotModelId = getModelId(slot.inventory?.model);
          if (!slot.inventory || slotModelId !== String(modelId)) return slot;
          if (!slot.inventory.model || typeof slot.inventory.model !== 'object') return slot;
          return {
            ...slot,
            inventory: {
              ...slot.inventory,
              model: { ...slot.inventory.model, virtualTourLink: link }
            }
          };
        })
      });

      try {
        const choices = JSON.parse(localStorage.getItem('app-choices') || '[]');
        const modelChoice = choices.find(choice => choice.key === 'model');
        if (modelChoice?.values) {
          modelChoice.values = modelChoice.values.map(model =>
            String(model._id) === String(modelId) ? { ...model, virtualTourLink: link } : model
          );
          localStorage.setItem('app-choices', JSON.stringify(choices));
        }
      } catch {
        // The open card still updates from component state if the cache cannot be written.
      }
    },
    [onSiteUpdate]
  );

  const loadFullInventoryUnit = async unitId => {
    const { response, error } = await makeApiRequests({
      endpoint: ENDPOINTS.INVENTORY_SEARCH_NEW,
      requestBody: {
        filter: { _id: unitId },
        includeContract: true
      }
    });

    if (error) {
      throw new Error(error);
    }

    const unit = response?.results?.[0];
    if (!unit) {
      throw new Error('Unit details could not be loaded.');
    }

    return withInventoryStatus(unit);
  };

  const handlePickerUnitSelect = useCallback(async () => {
    if (!selectedSlotInventory || !onPickerUnitSelect) return;

    const unitId = getInventoryUnitId(selectedSlotInventory);
    if (!unitId) {
      toast.error('Unable to identify this unit.');
      return;
    }

    if (isInventoryUnitExcluded(unitId, excludedUnitIds)) {
      toast.warning('This unit is already linked.');
      return;
    }

    let unit = selectedSlotInventory;
    const selectedSlot = site?.slots?.[selectedPolygonInfo?.polygonIndex];

    if (!selectedSlot?.inventoryFetched) {
      setSiteAddingUpdating(true);
      try {
        unit = await loadFullInventoryUnit(unitId);
      } catch (err) {
        toast.error(err.message);
        return;
      } finally {
        setSiteAddingUpdating(false);
      }
    }

    onPickerUnitSelect(unit);
    setSelectedPolygonInfo(null);
  }, [selectedSlotInventory, onPickerUnitSelect, excludedUnitIds, selectedPolygonInfo, site]);

  const handleWorksheetInventoryUpdate = updatedInventory => {
    onSiteUpdate({
      ...site,
      slots: site.slots.map(s =>
        s.inventory?._id === updatedInventory._id ? { ...s, inventory: updatedInventory, inventoryFetched: true } : s
      )
    });
    setWorksheetInventory(updatedInventory);
  };

  const selectedSlot = site?.slots?.[selectedPolygonInfo?.polygonIndex];
  const infoWindowToolbarExtra =
    !pickerMode && selectedSlotInventory && !selectedSlot?.isOfficeBuilding ? (
      <>
        <Button
          variant="warning"
          size="sm"
          className="info-window-toolbar-btn"
          disabled={siteAddingUpdating}
          onClick={handleCopyUnit}
        >
          <Clipboard size={14} />
          Copy
        </Button>
        <Button
          variant="info"
          size="sm"
          className="info-window-toolbar-btn"
          disabled={siteAddingUpdating}
          onClick={handleOpenCalculationWorksheet}
        >
          Calculation Worksheet
        </Button>
        <Button
          variant="info"
          size="sm"
          className="info-window-toolbar-btn"
          disabled={siteAddingUpdating}
          onClick={handleOpenCommissionSheet}
        >
          Commission Sheet
        </Button>
        {modelVirtualTourLink ? (
          <Button
            variant="info"
            size="sm"
            className="info-window-toolbar-btn"
            href={modelVirtualTourLink.startsWith('http') ? modelVirtualTourLink : `https://${modelVirtualTourLink}`}
            target="_blank"
            rel="noopener noreferrer"
            onClick={e => e.stopPropagation()}
          >
            Virtual Tour
          </Button>
        ) : (
          isAdmin() &&
          selectedModelId && (
            <Button
              variant="info"
              size="sm"
              className="info-window-toolbar-btn"
              disabled={siteAddingUpdating}
              onClick={e => {
                e.stopPropagation();
                setShowVirtualTourModal(true);
              }}
            >
              <Plus size={14} />
              Add Virtual Tour
            </Button>
          )
        )}
      </>
    ) : null;

  useEffect(() => {
    const fetchCompleteUnitDetails = async selectedSlot => {
      const unitId = selectedSlot?.inventory._id;

      const { response, error } = await makeApiRequests({
        endpoint: ENDPOINTS.INVENTORY_SEARCH_NEW,
        requestBody: {
          filter: {
            _id: unitId
          },
          includeContract: true
        }
      });

      if (error) {
        return;
      }

      const unit = response.results[0];
      unit.inventoryStatus = unit['contract'] ? 'Contracted' : unit['isRSO']?.toLowerCase() === 'yes' ? 'RSO' : 'Stock';
      onSiteUpdate({
        ...site,
        slots: site.slots.map(s =>
          s._id === selectedSlot._id
            ? {
                ...s,
                inventory: unit,
                inventoryFetched: true
              }
            : s
        )
      });
    };

    let selectedSlot = site?.slots?.[selectedPolygonInfo?.polygonIndex] || null;

    if (selectedSlot?.inventory && !selectedSlot?.inventoryFetched) {
      fetchCompleteUnitDetails(selectedSlot);
    }
  }, [selectedPolygonInfo, site]);

  const InfoWindowContent = useCallback(() => {
    let selectedSlot = site?.slots?.[selectedPolygonInfo?.polygonIndex] || null;

    if (selectedSlot) {
      selectedSlot = {
        ...selectedSlot,
        siteId: site.name
      };
    }

    if (pickerMode && selectedSlot) {
      return (
        <SiteMapPickerSlotPanel
          slot={selectedSlot}
          selecting={siteAddingUpdating}
          onSelectUnit={handlePickerUnitSelect}
          onInventoryUpdate={handleWorksheetInventoryUpdate}
        />
      );
    }

    return (
      <div className="pt-2 border-top">
        {selectedSlot?.isOfficeBuilding ? (
          <div style={{ width: 360 }}>
            <h6 className="smallFont py-2">This is an Office Building</h6>{' '}
            {isAdmin() && (
              <div className="border-top d-flex gap-2 justify-content-end mt-1 pt-2">
                <Button
                  variant="danger"
                  className="d-flex gap-1 align-items-center mr-1"
                  size="sm"
                  disabled={siteAddingUpdating}
                  onClick={() =>
                    setCreateSlotModalMeta({
                      paths: selectedSlot.paths,
                      existingSlot: selectedSlot,
                      modalAction: 'edit'
                    })
                  }
                >
                  <Pencil size={14} />
                  <h6 className="smallFont mx-1 mb-0">Edit</h6>
                </Button>

                <Button
                  variant="danger"
                  disabled={siteAddingUpdating}
                  className="d-flex gap-1 align-items-center"
                  size="sm"
                  onClick={() => onDeleteSlotClick(selectedSlot)}
                >
                  <Trash size={14} />
                  <h6 className="smallFont mx-1 mb-0">Delete</h6>
                </Button>
              </div>
            )}
          </div>
        ) : selectedSlot?.inventory ? (
          <div style={{ minWidth: 'clamp(360px, 85vw, 580px)' }}>
            <h6 className="smallFont">Unit at this slot</h6>{' '}
            <div className="my-2 mx-3">
              {' '}
              <InventorySearchItem
                mini={true}
                inventory={selectedSlot?.inventory}
                slotNumber={selectedSlot?.number}
                siteId={selectedSlot?.siteId}
                showLocationDetails={true}
                selected
                fromMap={true}
                coverImageEnabled
                onInventoryUpdate={handleWorksheetInventoryUpdate}
              />
            </div>
            <div className="border-top d-flex flex-wrap justify-content-end gap-2 mt-1 pt-1">
              <Button
                variant="danger"
                className="d-flex gap-1 align-items-center"
                size="sm"
                disabled={siteAddingUpdating}
                onClick={() =>
                  setCreateSlotModalMeta({
                    paths: selectedSlot.paths,
                    existingSlot: selectedSlot,
                    modalAction: 'edit'
                  })
                }
              >
                <Pencil size={14} />
                <h6 className="smallFont mb-0">Edit</h6>
              </Button>
              <Button
                variant="danger"
                className="d-flex gap-1 align-items-center"
                size="sm"
                disabled={siteAddingUpdating}
                onClick={() => cloneSlot(selectedSlot)}
              >
                <Clipboard size={14} />
                <h6 className="smallFont mb-0">Clone</h6>
              </Button>
              <Button
                size="sm"
                disabled={siteAddingUpdating}
                variant="danger"
                onClick={() => removeUnitFromSlot(selectedSlot)}
              >
                <h6 className="smallFont mb-0">Remove Unit</h6>
              </Button>
              <Button
                size="sm"
                disabled={siteAddingUpdating}
                onClick={() =>
                  setInventoryUnitMeta({
                    slot: selectedSlot
                  })
                }
              >
                <h6 className="smallFont mb-0">Assign another unit</h6>
              </Button>
              {isAdmin() && modelVirtualTourLink && (
                <Button
                  size="sm"
                  className="d-flex gap-1 align-items-center"
                  variant="info"
                  disabled={siteAddingUpdating}
                  onClick={() => setShowVirtualTourModal(true)}
                >
                  <Pencil size={14} />
                  <h6 className="smallFont mb-0">Edit Virtual Tour</h6>
                </Button>
              )}
            </div>
          </div>
        ) : (
          <>
            <div style={{ width: 360 }} className="d-flex flex-column justify-content-center align-items-center my-5">
              <h6 className="smallFont">No any unit at this slot.</h6>
              <Button
                size="sm"
                className=""
                disabled={siteAddingUpdating}
                onClick={() =>
                  setInventoryUnitMeta({
                    slot: selectedSlot
                  })
                }
              >
                <h6 className="smallFont mb-0">Assign unit</h6>
              </Button>
            </div>{' '}
            {isAdmin() && (
              <div className="border-top d-flex justify-content-between mt-1 pt-1">
                <CustomMultiSelect
                  isMulti={false}
                  selectedItems={selectedSlot?.status}
                  items={SLOT_STATUS?.map(s => ({ label: s, value: s }))}
                  height="25px"
                  isLoading={siteAddingUpdating}
                  closeMenuOnSelect
                  onChange={status => onPolygonUpdate({ ...selectedSlot, status })}
                />
                <Button
                  variant="danger"
                  className="d-flex gap-1 align-items-center "
                  size="sm"
                  disabled={siteAddingUpdating}
                  onClick={() => cloneSlot(selectedSlot)}
                >
                  <Clipboard size={14} />
                  <h6 className="smallFont mx-1 mb-0">Clone</h6>
                </Button>{' '}
                <Button
                  variant="danger"
                  className="d-flex gap-1 align-items-center"
                  size="sm"
                  disabled={siteAddingUpdating}
                  onClick={() =>
                    setCreateSlotModalMeta({
                      paths: selectedSlot.paths,
                      existingSlot: selectedSlot,
                      modalAction: 'edit'
                    })
                  }
                >
                  <Pencil size={14} />
                  <h6 className="smallFont mx-1 mb-0">Edit</h6>
                </Button>
                <Button
                  variant="danger"
                  disabled={siteAddingUpdating}
                  className="d-flex gap-1 align-items-center"
                  size="sm"
                  onClick={() => onDeleteSlotClick(selectedSlot)}
                >
                  <Trash size={14} />
                  <h6 className="smallFont mx-1 mb-0">Delete</h6>
                </Button>
              </div>
            )}
          </>
        )}
      </div>
    );
  }, [
    selectedPolygonInfo,
    site,
    siteAddingUpdating,
    pickerMode,
    excludedUnitIds,
    handlePickerUnitSelect,
    modelVirtualTourLink
  ]);

  return (
    <div className="h-100 w-100 d-flex flex-column">
      <div className="flex-grow-1">
        <GoogleMap
          center={site.coordinates}
          zoom={site.scale}
          rotation={site.rotation}
          polygons={site.slots}
          handlePolygonClick={handlePolygonClick}
          infoWindowMeta={selectedPolygonInfo}
          setInfoWindowMeta={setSelectedPolygonInfo}
          disableDrawing={siteAddingUpdating || pickerMode}
          onMapPropertiesChange={setNewMapProperties}
          onAddNewPolygon={onAddNewPolygon}
          onPolygonUpdate={onPolygonUpdate}
          InfoWindowContent={InfoWindowContent}
          infoWindowToolbarExtra={infoWindowToolbarExtra}
          hideInfoWindowToolbar={pickerMode}
        />
      </div>
      {!pickerMode && (
        <div className="d-flex justify-content-end align-items-center gap-2 p-2">
          {isAdmin() && (
            <Button
              size="sm"
              className="d-flex align-items-center"
              variant="success"
              onClick={updateSiteDefaultView}
              disabled={!newMapProperties || siteAddingUpdating}
            >
              <PinAngle className="mx-1" />
              Set As Default View
            </Button>
          )}
          <Button size="sm" className="d-flex align-items-center" variant="primary" onClick={onPrint}>
            <Printer className="mx-1" />
            Print
          </Button>
        </div>
      )}
      {!pickerMode && (
        <>
          <InventoryPicker
            show={inventoryUnitMeta}
            onSubmit={assignUnitToSlot}
            submitting={siteAddingUpdating}
            fromContracts={false}
            fromMap={true}
            onInventoryPickerClose={onInventoryPickerClose}
            selectedUnits={inventoryUnitMeta?.selectedUnit ? [inventoryUnitMeta.selectedUnit] : []}
            onUnitSelect={inv => setInventoryUnitMeta({ ...inventoryUnitMeta, selectedUnit: inv })}
          />
          <AlertModal
            alertText={'Are you sure you want to delete this slot?'}
            show={Boolean(deleteSlotMeta)}
            showProgress={deleteSlotMeta?.showProgress}
            onDismissClick={() => setDeleteSlotMeta(null)}
            onHide={() => setDeleteSlotMeta(null)}
            onContinueClick={deleteSlot}
          />
          <SlotNumberModal
            onSubmit={slotNumber => handleSlotNumberSubmit(slotNumber, createSlotModalMeta.existingSlot)}
            onHide={() => setCreateSlotModalMeta(null)}
            show={Boolean(createSlotModalMeta)}
            progressState={progressState}
            setOfficelBuilding={setIsOfficeBuilding}
            officeBuilding={isOfficeBuilding}
            initialSlotNumber={createSlotModalMeta?.existingSlot?.number}
            initialIsOfficeBuilding={createSlotModalMeta?.existingSlot?.isOfficeBuilding}
            modalAction={createSlotModalMeta?.modalAction || 'create'}
          />
          <CalculationWorksheetSidebar
            visible={showCalculationWorksheet}
            onClose={() => setShowCalculationWorksheet(false)}
            inventory={worksheetInventory}
            appChoices={appChoices}
            onInventoryUpdate={handleWorksheetInventoryUpdate}
          />
          <CommissionSheetSidebar
            visible={showCommissionSheet}
            onClose={() => setShowCommissionSheet(false)}
            inventory={showCommissionSheet ? selectedSlotInventory : null}
            appChoices={appChoices}
          />
          <SiteMapVirtualTourModal
            show={showVirtualTourModal}
            modelId={selectedModelId}
            link={modelVirtualTourLink}
            onHide={() => setShowVirtualTourModal(false)}
            onSaved={handleVirtualTourSaved}
          />
        </>
      )}
    </div>
  );
};

export default SiteMap;
